import { useEffect, useMemo, useState } from "react";
import "../../styles/analytics.css";
import { fetchAnalytics, ApiClientError, isMockMode } from "../../api/client";
import type { AnalyticsResponse, Channel } from "../../types/analytics";
import { jstTodayYmd } from "../../lib/dateRange";
import { presetRange, type Preset } from "../../lib/analyticsCalc";
import { StateBanner } from "../../components/StateBanner";
import { FilterBar } from "./FilterBar";
import { KpiCards } from "./KpiCards";
import { ChannelCards } from "./ChannelCards";
import { TrendChart } from "./TrendChart";
import { SourcesCard } from "./SourcesCard";
import { Breakdown, type BreakdownTab, type SortState } from "./Breakdown";

/** mockモードのときだけfixtureの最終日(2026-09-25)に固定する(P/L画面のFIXTURE_TODAYと同じ方針)。 */
const FIXTURE_TODAY = "2026-09-25";

function computeToday(): string {
  return isMockMode() ? FIXTURE_TODAY : jstTodayYmd();
}

type Loadable<T> = { status: "loading" } | { status: "error"; message: string } | { status: "ok"; data: T };

export function AnalyticsPage() {
  const [today] = useState<string>(() => computeToday());
  const [preset, setPreset] = useState<Preset>("30d");
  const initial = useMemo(() => presetRange("30d", today), [today]);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [granularity, setGranularity] = useState<"day" | "week" | "month">(initial.granularity);
  const [channels, setChannels] = useState<Record<Channel, boolean>>({ shopify: true, rakuten: true, amazon: true });
  const [product, setProduct] = useState<string | null>(null);
  const [chartMetric, setChartMetric] = useState<"sales" | "sessions" | "orders" | "ad">("sales");
  const [showCvrLine, setShowCvrLine] = useState(true);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [breakdownTab, setBreakdownTab] = useState<BreakdownTab>("product");
  const [sort, setSort] = useState<SortState>({ key: "sales", dir: -1 });

  const [state, setState] = useState<Loadable<AnalyticsResponse>>({ status: "loading" });

  useEffect(() => {
    setState({ status: "loading" });
    fetchAnalytics({ from, to, granularity, product })
      .then((data) => setState({ status: "ok", data }))
      .catch((e: unknown) => setState({ status: "error", message: e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。" }));
  }, [from, to, granularity, product]);

  const onPreset = (p: Preset) => {
    setPreset(p);
    setHoverIndex(null);
    if (p === "custom") return;
    const r = presetRange(p, today);
    setFrom(r.from);
    setTo(r.to);
    setGranularity(r.granularity);
  };

  const onCustomFrom = (v: string) => {
    setPreset("custom");
    setHoverIndex(null);
    setFrom(v > to ? to : v);
  };
  const onCustomTo = (v: string) => {
    setPreset("custom");
    setHoverIndex(null);
    setTo(v < from ? from : v);
  };

  const toggleChannel = (ch: Channel) => {
    setChannels((prev) => {
      const next = { ...prev, [ch]: !prev[ch] };
      if (!Object.values(next).some(Boolean)) return prev; // 最低1つはON
      return next;
    });
  };

  const activeChannels = useMemo(() => (Object.keys(channels) as Channel[]).filter((c) => channels[c]), [channels]);

  const productName = state.status === "ok" ? state.data.products.find((p) => p.key === product)?.name ?? product : product;

  return (
    <div className="an-root">
      <div className="an-shell">
        <div>
          <h1 className="an-page-title">転換率・売上分析</h1>
          <p className="an-page-desc">Shopify・楽天市場・Amazon の売上、アクセス、転換率、広告費を期間・チャネル・商品で比較します。</p>
        </div>

        {state.status === "ok" && state.data.notes.length > 0 && (
          <div className="an-notes">
            {state.data.notes.map((n, i) => (
              <div key={`note-${i}`}>・{n}</div>
            ))}
          </div>
        )}

        <FilterBar
          preset={preset}
          from={from}
          to={to}
          minDate="2026-01-01"
          maxDate={today}
          granularity={granularity}
          channels={channels}
          productName={product ? productName ?? product : null}
          onPreset={onPreset}
          onCustomFrom={onCustomFrom}
          onCustomTo={onCustomTo}
          onGranularity={(g) => setGranularity(g)}
          onToggleChannel={toggleChannel}
          onClearProduct={() => setProduct(null)}
        />

        {state.status === "loading" && <StateBanner kind="loading" message="読み込み中" />}
        {state.status === "error" && <StateBanner kind="error" message={state.message} />}

        {state.status === "ok" && (
          <>
            <KpiCards totals={state.data.totals} prevTotals={state.data.prev_totals} activeChannels={activeChannels} hasPrev={state.data.prev_range.from <= state.data.prev_range.to} />

            <ChannelCards totals={state.data.totals} prevTotals={state.data.prev_totals} channels={channels} hasPrev={true} />

            <div className="an-trend-row">
              <TrendChart
                buckets={state.data.buckets}
                channels={channels}
                metric={chartMetric}
                onMetricChange={setChartMetric}
                showCvrLine={showCvrLine}
                onToggleCvrLine={() => setShowCvrLine((v) => !v)}
                hoverIndex={hoverIndex}
                onHover={setHoverIndex}
              />
              <SourcesCard sources={state.data.sources} channels={channels} onSeeAll={() => setBreakdownTab("source")} />
            </div>

            <Breakdown
              tab={breakdownTab}
              onTabChange={setBreakdownTab}
              productsTable={state.data.products_table}
              sources={state.data.sources}
              buckets={state.data.buckets}
              keywords={state.data.keywords}
              channels={channels}
              granularity={granularity}
              sort={sort}
              onSortChange={setSort}
              selectedProduct={product}
              onSelectProduct={(key) => setProduct((prev) => (prev === key ? null : key))}
            />
          </>
        )}
      </div>
    </div>
  );
}
